/**
 * Phone validation, masking, and E.164 normalisation backed by Google
 * libphonenumber via the `libphonenumber-js` port (IMM-202).
 *
 * Port choice: `libphonenumber-js` over the full `google-libphonenumber` — it
 * is tree-shakeable, ships an `AsYouType` formatter, and parses cleanly without
 * a Closure runtime. The `/max` metadata is imported deliberately so `isValid`
 * reflects per-type number lengths (a length heuristic is no longer enough);
 * this is the single source of frontend phone validity, replacing the former
 * `phone.length < 4` check across every entry point.
 */
import { AsYouType, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/max';

/**
 * Default region for bare national numbers. A number prefixed with an explicit
 * `+<country code>` overrides this; anything else is interpreted as German.
 */
export const DEFAULT_PHONE_REGION: CountryCode = 'DE';

/**
 * Format a phone number as the user types, switching the mask to the region
 * implied by an explicit `+<country code>` and otherwise assuming
 * {@link DEFAULT_PHONE_REGION}. Returns the input verbatim when it cannot yet
 * be formatted, so the caret-sensitive raw value is never swallowed.
 */
export function formatPhoneAsYouType(input: string): string {
  if (input.trim() === '') return input;

  const formatted = new AsYouType(DEFAULT_PHONE_REGION).input(input);

  return formatted === '' ? input : formatted;
}

/**
 * Resolve the region a (possibly partial) input currently maps to — the
 * explicit `+<country code>` region if present, otherwise the default. Drives
 * the flag adornment; never throws on partial input.
 */
export function resolvePhoneRegion(input: string): CountryCode {
  if (input.trim() === '') return DEFAULT_PHONE_REGION;

  const formatter = new AsYouType(DEFAULT_PHONE_REGION);
  formatter.input(input);

  return formatter.getCountry() ?? DEFAULT_PHONE_REGION;
}

/**
 * Region the input *explicitly* identifies via a leading `+<country code>`, or
 * `null` when the number carries no country code yet. Unlike
 * {@link resolvePhoneRegion} this never assumes {@link DEFAULT_PHONE_REGION} —
 * so the flag adornment only appears once a locale is actually identified.
 */
export function resolveExplicitPhoneRegion(input: string): CountryCode | null {
  if (!input.trim().startsWith('+')) return null;

  const formatter = new AsYouType(DEFAULT_PHONE_REGION);
  formatter.input(input);

  return formatter.getCountry() ?? null;
}

/**
 * Whether a non-empty value is a valid phone number per libphonenumber. A
 * blank value is reported valid here — callers decide whether empty is
 * allowed; emptiness is never treated as "invalid".
 */
export function isValidPhone(input: string): boolean {
  if (input.trim() === '') return true;

  const parsed = parsePhoneNumberFromString(input, DEFAULT_PHONE_REGION);

  return parsed?.isValid() ?? false;
}

/**
 * Normalise a valid number to its canonical E.164 form (`+49…`). Returns `null`
 * for blank input and for anything libphonenumber cannot parse to a valid
 * number — callers gate on validity before persisting.
 */
export function toE164(input: string): string | null {
  if (input.trim() === '') return null;

  const parsed = parsePhoneNumberFromString(input, DEFAULT_PHONE_REGION);

  if (parsed === undefined || !parsed.isValid()) return null;

  return parsed.number;
}

const REGIONAL_INDICATOR_OFFSET = 0x1f1e6;
const UPPERCASE_A = 65;

/**
 * Map an ISO 3166-1 alpha-2 region to its flag emoji via Unicode regional
 * indicator symbols — no flag-image asset or extra dependency. A two-letter
 * code maps to the two indicator code points the platform renders as a flag.
 */
export function regionToFlagEmoji(region: CountryCode): string {
  return [...region.toUpperCase()]
    .map((char) => String.fromCodePoint(REGIONAL_INDICATOR_OFFSET + (char.charCodeAt(0) - UPPERCASE_A)))
    .join('');
}

/**
 * The `href` of a click-to-call link. The stored value is normalised to E.164 so
 * the dialler receives an unambiguous number; a value libphonenumber cannot
 * parse (a legacy free-text number predating validation) falls back to the
 * stored string with its whitespace stripped, which every user agent still
 * dials. Pair it with {@link formatPhoneForDisplay} for the visible label — the
 * href is unformatted, the label is formatted.
 */
export function telHref(stored: string): string {
  return `tel:${toE164(stored) ?? stored.replace(/\s+/g, '')}`;
}

/**
 * Render a stored value for display in an input. A parseable number is shown in
 * its readable national/international shape; anything unparseable — e.g. a
 * legacy free-text number predating this validation — is returned untouched so
 * loading a contact never hard-errors.
 */
export function formatPhoneForDisplay(stored: string): string {
  if (stored.trim() === '') return stored;

  const parsed = parsePhoneNumberFromString(stored, DEFAULT_PHONE_REGION);

  if (parsed === undefined) return stored;

  return parsed.country === DEFAULT_PHONE_REGION ? parsed.formatNational() : parsed.formatInternational();
}
